/**
 * 2.668 — read a cursor-paged route TO THE END, for a caller that treats the
 * result as the whole answer (a session's projects, a picker, a count).
 *
 * The defect this exists for: a first page read as the complete list. It looks
 * complete, it is not, and nothing on screen says so. `listRoles` did it (fixed
 * at 2.663), and so did the session's project list, which every project-scoped
 * screen inherits.
 *
 * BOUNDED (invariant 10): at most `MAX_PAGES` requests. Beyond that it THROWS
 * rather than returning a prefix, because a list that looks complete and is not
 * is exactly the failure being fixed. A caller that wants a page at a time with
 * "Load more" should not use this.
 */
export const MAX_PAGES = 20;

export interface CursorPage<T> {
  items?: T[];
  next_cursor?: string;
}

export async function readAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  what: string,
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = await fetchPage(cursor);
    const items = page.items ?? [];
    out.push(...items);
    cursor = page.next_cursor || undefined;
    // 2.796: the server returns a cursor on any FULL page, so a list exactly a
    // page long is followed by an empty page; an empty page ends the read
    // whatever cursor it carries, rather than spending the cap on it.
    if (!cursor || items.length === 0) return out;
  }
  throw new Error(`More than ${MAX_PAGES} pages of ${what}; the list is not shown rather than shown in part.`);
}

/**
 * The same, for a route that pages by `before` = the oldest row's `seq`,
 * newest first, at a fixed page size (risk-weight history, 50 per page). A
 * short page is the last one.
 */
export async function readAllBefore<T extends { seq: number }>(
  fetchPage: (before: number | undefined) => Promise<T[] | null | undefined>,
  pageSize: number,
  what: string,
): Promise<T[]> {
  const out: T[] = [];
  let before: number | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = (await fetchPage(before)) ?? [];
    out.push(...page);
    if (page.length < pageSize) return out;
    before = page[page.length - 1].seq;
    if (before <= 1) return out;
  }
  throw new Error(`More than ${MAX_PAGES} pages of ${what}; the list is not shown rather than shown in part.`);
}
